import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Form, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm } from "components";
import { createFormHelper } from "lib/Form";
import { TYRETEAM_CHANNELS } from "models";
import {
  useCreateTyreTeamCredentialMutation,
  useDeleteTyreTeamCredentialMutation,
  useUpdateTyreTeamCredentialMutation,
  useValidateTyreTeamCredentialMutation
} from "modules/DealerLocationDetails/components/Tyres/components/TyreTeamCredentialsModal/hooks";

import type { DealerLocationID, TyreTeamCredential } from "models";

type TyreTeamCredentialFormPayload = Omit<TyreTeamCredential, "id" | "dealer_location_id">;

const TYRE_TEAM_CREDENTIALS_FORM_ID = "tyre-team-credentials-form";
const FormHelper = createFormHelper<TyreTeamCredentialFormPayload>();

const getDefaultFormValues = (credential?: TyreTeamCredential | null): TyreTeamCredentialFormPayload => ({
  channel: credential?.channel ?? TYRETEAM_CHANNELS.WheelTyre,
  login: credential?.login ?? "",
  password: credential?.password ?? "",
  location_id: credential?.location_id ?? "",
  customer_id: credential?.customer_id ?? ""
});

const isFormInvalid = ({ login, password, location_id, customer_id }: TyreTeamCredentialFormPayload) => !(login && password && location_id && customer_id);

type TyreTeamCredentialsModalProps = {
  isOpen: boolean;
  locationId: DealerLocationID;
  credential: TyreTeamCredential | null;
  onClose: () => void;
};

export const TyreTeamCredentialsModal = ({ isOpen, locationId, credential, onClose }: TyreTeamCredentialsModalProps) => {
  const { t } = useTranslation();

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { mutate: createCredential, isPending: isCreateCredentialPending } = useCreateTyreTeamCredentialMutation();
  const { mutate: updateCredential, isPending: isUpdateCredentialPending } = useUpdateTyreTeamCredentialMutation();
  const { mutate: deleteCredential, isPending: isDeleteCredentialPending, error: deleteCredentialError } = useDeleteTyreTeamCredentialMutation();
  const { mutate: validateCredential, isPending: isValidateCredentialPending } = useValidateTyreTeamCredentialMutation();

  const formMethods = FormHelper.useForm({ values: getDefaultFormValues(credential), mode: "all" });

  const channelOptions = [
    { key: TYRETEAM_CHANNELS.WheelTyre, value: TYRETEAM_CHANNELS.WheelTyre, text: "Wheel-Tyre" },
    { key: TYRETEAM_CHANNELS.BandenExpress, value: TYRETEAM_CHANNELS.BandenExpress, text: "Banden Express" }
  ];

  const isSavePending = isCreateCredentialPending || isUpdateCredentialPending;
  const values = formMethods.watch();

  const handleClose = () => {
    formMethods.reset(getDefaultFormValues());
    onClose();
  };

  const handleSubmit = (formValues: TyreTeamCredentialFormPayload) => {
    if (isFormInvalid(formValues)) return;

    if (credential) {
      updateCredential({ ...credential, ...formValues }, { onSuccess: handleClose });
      return;
    }

    createCredential({ ...formValues, dealer_location_id: locationId }, { onSuccess: handleClose });
  };

  const handleValidate = () => {
    if (!credential) return;

    validateCredential(credential.id, { onSuccess: () => toast.success(t("v8_tyreteam_credentials_valid", "TyreTeam credentials are valid")) });
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.stopPropagation();
    formMethods.handleSubmit(handleSubmit)(event);
  };

  return (
    <Modal open={isOpen} size="small" closeOnDimmerClick={false} onClose={handleClose} className="TyreTeamCredentialsModal">
      <FormHelper.FormProvider {...formMethods}>
        <Modal.Header>
          <div>{credential ? t("v8_edit_credentials", "Edit credentials") : t("v8_add_new_credentials", "Add new credentials")}</div>
          <Button className="modal-close-btn" basic type="button" onClick={handleClose}>
            <Icon className="xmark" />
          </Button>
        </Modal.Header>

        <Modal.Content>
          <Form id={TYRE_TEAM_CREDENTIALS_FORM_ID} onSubmit={handleFormSubmit} disabled={isSavePending}>
            <Form.Group widths="equal">
              <Form.Field required>
                <label>{t("v8_channel", "Channel")}</label>
                <FormHelper.Field.Dropdown name="channel" options={channelOptions} />
              </Form.Field>
              <Form.Field required error={formMethods.formState.isSubmitted && !values.login}>
                <label>{t("v8_login", "Login")}</label>
                <FormHelper.Field.Input name="login" preventAutofill />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field required error={formMethods.formState.isSubmitted && !values.password}>
                <label>{t("v8_password", "Password")}</label>
                <FormHelper.Field.Input name="password" preventAutofill inputProps={{ type: "password" }} />
              </Form.Field>
              <Form.Field required error={formMethods.formState.isSubmitted && !values.location_id}>
                <label>{t("v8_location_id", "Location ID")}</label>
                <FormHelper.Field.Input name="location_id" />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field required error={formMethods.formState.isSubmitted && !values.customer_id}>
                <label>{t("v8_customer_id", "Customer ID")}</label>
                <FormHelper.Field.Input name="customer_id" />
              </Form.Field>
            </Form.Group>
          </Form>
        </Modal.Content>

        <Modal.Actions>
          <div className="action-buttons-container">
            {!!credential && (
              <Button type="button" color="red" floated="left" disabled={isSavePending} onClick={() => setIsDeleteConfirmOpen(true)}>
                {t("v8_delete", "Delete")}
                <Icon className="trash" />
              </Button>
            )}
            <Button type="button" className="light" onClick={handleClose} disabled={isSavePending}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Button>
            {!!credential && (
              <Button type="button" color="yellow" loading={isValidateCredentialPending} disabled={isValidateCredentialPending || isSavePending} onClick={handleValidate}>
                {t("v8_validate", "Validate")}
              </Button>
            )}
            <Button type="submit" form={TYRE_TEAM_CREDENTIALS_FORM_ID} color="green" disabled={isSavePending} loading={isSavePending}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Button>
          </div>
        </Modal.Actions>
      </FormHelper.FormProvider>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={isDeleteConfirmOpen}
        isLoading={isDeleteCredentialPending}
        error={deleteCredentialError?.message}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleCancel={() => setIsDeleteConfirmOpen(false)}
        handleConfirm={() =>
          credential &&
          deleteCredential(credential, {
            onSuccess: () => {
              setIsDeleteConfirmOpen(false);
              handleClose();
            }
          })
        }
      />
    </Modal>
  );
};
